import { layoutWithLines, prepareWithSegments } from "@chenglou/pretext";
import {
    materializeRichInlineLineRange,
    prepareRichInline,
    type RichInlineItem,
    walkRichInlineLineRanges,
} from "@chenglou/pretext/rich-inline";
import { motion } from "motion/react";
import { Fragment, type ReactNode, useMemo } from "react";

export const WORD_EASING = [0.22, 1, 0.36, 1] as const;
export const WORD_STAGGER_S = 0.008;
export const LINE_STAGGER_S = 0.03;
export const BLOCK_GAP_S = 0.05;
export const TEXT_REVEAL_Y = 12;

export type AnimatedWordsDirection = "forward" | "backward";

export type DirectionalWordsCustom = {
    direction: AnimatedWordsDirection;
    delayS: number;
};

export const directionalWordVariants = {
    initial: (direction: AnimatedWordsDirection) => ({
        opacity: 0,
        y: direction === "forward" ? TEXT_REVEAL_Y : -TEXT_REVEAL_Y,
    }),
    animate: {
        opacity: 1,
        y: 0,
        transition: {
            opacity: { duration: 0.18, ease: "easeOut" as const },
            y: { duration: 0.28, ease: WORD_EASING },
        },
    },
    exit: (direction: AnimatedWordsDirection) => ({
        opacity: 0,
        y: direction === "forward" ? -TEXT_REVEAL_Y : TEXT_REVEAL_Y,
        transition: {
            opacity: { duration: 0.18, ease: "easeOut" as const },
            y: { duration: 0.28, ease: WORD_EASING },
        },
    }),
};

export const directionalWordsContainerVariants = {
    initial: {},
    animate: ({ delayS }: DirectionalWordsCustom) => ({
        transition: {
            staggerChildren: WORD_STAGGER_S,
            delayChildren: delayS,
        },
    }),
    exit: ({ direction }: DirectionalWordsCustom) => ({
        transition: {
            staggerChildren: WORD_STAGGER_S,
            staggerDirection: direction === "forward" ? 1 : -1,
        },
    }),
};

/** Keeps AnimatePresence mounted until nested AnimatedWords finish exiting. */
export const slideTextPresenceExit = {
    opacity: 1,
    transition: { when: "afterChildren" as const, duration: 0 },
};

function revealTransition(delayS: number) {
    return {
        opacity: { duration: 0.18, ease: "easeOut" as const, delay: delayS },
        y: { duration: 0.28, ease: WORD_EASING, delay: delayS },
    };
}

function getDirectionalOffsets(direction: AnimatedWordsDirection) {
    return direction === "forward"
        ? { enterY: TEXT_REVEAL_Y, exitY: -TEXT_REVEAL_Y }
        : { enterY: -TEXT_REVEAL_Y, exitY: TEXT_REVEAL_Y };
}

/** `inline` keeps real spaces between words so spacing follows the font. */
type AnimatedWordsLayout = "flex" | "inline";

type AnimatedWordsProps = {
    text: string;
    delayS?: number;
    className?: string;
    direction?: AnimatedWordsDirection;
    layout?: AnimatedWordsLayout;
};

export function AnimatedWords({
    text,
    delayS = 0,
    className,
    direction,
    layout = "flex",
}: AnimatedWordsProps) {
    const words = usePretextWords(text);
    const containerStyle =
        layout === "inline"
            ? { display: "inline" as const }
            : { display: "flex" as const, flexWrap: "wrap" as const, gap: "0 4.5px" };

    if (direction) {
        return (
            <motion.span
                className={className}
                custom={{ direction, delayS } satisfies DirectionalWordsCustom}
                variants={directionalWordsContainerVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                style={containerStyle}
            >
                {words.map((word, i) => (
                    <Fragment key={word.id}>
                        {layout === "inline" && i > 0 ? " " : null}
                        <motion.span
                            custom={direction}
                            variants={directionalWordVariants}
                            style={{ display: "inline-block", verticalAlign: "bottom" }}
                        >
                            {word.text}
                        </motion.span>
                    </Fragment>
                ))}
            </motion.span>
        );
    }

    return (
        <span className={className} style={containerStyle}>
            {words.map((word, i) => (
                <Fragment key={word.id}>
                    {layout === "inline" && i > 0 ? " " : null}
                    <span style={{ display: "inline-block", verticalAlign: "bottom" }}>
                        <motion.span
                            initial={{ opacity: 0, y: TEXT_REVEAL_Y }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={revealTransition(delayS + i * WORD_STAGGER_S)}
                            style={{ display: "inline-block" }}
                        >
                            {word.text}
                        </motion.span>
                    </span>
                </Fragment>
            ))}
        </span>
    );
}

type AnimatedLinesProps = {
    text: string;
    font: string;
    maxWidth: number;
    lineHeight: number;
    delayS?: number;
    className?: string;
};

export function AnimatedLines({
    text,
    font,
    maxWidth,
    lineHeight,
    delayS = 0,
    className,
}: AnimatedLinesProps) {
    const lines = usePretextLines(text, font, maxWidth, lineHeight);

    return (
        <span className={className} style={{ display: "block" }}>
            {lines.map((line) => (
                <span key={line.id} style={{ display: "block" }}>
                    <motion.span
                        initial={{ opacity: 0, y: TEXT_REVEAL_Y }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={revealTransition(
                            delayS + line.index * LINE_STAGGER_S,
                        )}
                        style={{ display: "block" }}
                    >
                        {line.text}
                    </motion.span>
                </span>
            ))}
        </span>
    );
}

type LinkMeta = { href: string; className?: string };

type AnimatedHTMLLinesProps = {
    html: string;
    font: string;
    maxWidth: number;
    delayS?: number;
    className?: string;
};

export function AnimatedHTMLLines({
    html,
    font,
    maxWidth,
    delayS = 0,
    className,
}: AnimatedHTMLLinesProps) {
    const { lines, links } = useRichHTMLLines(html, font, maxWidth);

    return (
        <span className={className} style={{ display: "block" }}>
            {lines.map((line, lineIdx) => (
                <span
                    key={line.map((f) => f.text).join("")}
                    style={{ display: "block" }}
                >
                    <motion.span
                        initial={{ opacity: 0, y: TEXT_REVEAL_Y }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={revealTransition(delayS + lineIdx * LINE_STAGGER_S)}
                        style={{ display: "block" }}
                    >
                        {line.map((frag) => {
                            const link = links.get(frag.itemIndex);
                            return link ? (
                                <a
                                    key={frag.key}
                                    href={link.href}
                                    className={link.className}
                                >
                                    {frag.text}
                                </a>
                            ) : (
                                <span key={frag.key}>{frag.text}</span>
                            );
                        })}
                    </motion.span>
                </span>
            ))}
        </span>
    );
}

export type StrongHTMLLineFragment = {
    key: string;
    text: string;
    strong: boolean;
    gapBefore: number;
};

const STRONG_HTML_LEADING_SPACE_RE = /^[ \t]+/;
const STRONG_HTML_TRAILING_SPACE_RE = /[ \t]+$/;

type StrongHTMLLineMeasure = {
    regularFont: string;
    strongFont: string;
    maxWidth: number;
};

type StrongHTMLLinesProps = {
    html: string;
    measure: StrongHTMLLineMeasure;
    className?: string;
};

export function StrongHTMLLines({ html, measure, className }: StrongHTMLLinesProps) {
    const lines = useStrongHTMLLines(html, measure);

    return (
        <span className={className} style={{ display: "block" }}>
            {lines.map((line) => (
                <span
                    key={line.map((fragment) => fragment.text).join("")}
                    style={{ display: "block" }}
                >
                    {line.map((fragment) => (
                        <StrongHTMLLineFragmentView
                            key={fragment.key}
                            fragment={fragment}
                        />
                    ))}
                </span>
            ))}
        </span>
    );
}

type AnimatedStrongHTMLLinesProps = StrongHTMLLinesProps & {
    delayS?: number;
    direction?: AnimatedWordsDirection;
};

export function AnimatedStrongHTMLLines({
    html,
    measure,
    delayS = 0,
    direction,
    className,
}: AnimatedStrongHTMLLinesProps) {
    const lines = useStrongHTMLLines(html, measure);
    const offsets = direction ? getDirectionalOffsets(direction) : null;

    return (
        <span className={className} style={{ display: "block" }}>
            {lines.map((line, lineIdx) => (
                <span
                    key={line.map((fragment) => fragment.text).join("")}
                    style={{ display: "block" }}
                >
                    <motion.span
                        initial={
                            offsets
                                ? { opacity: 0, y: offsets.enterY }
                                : { opacity: 0, y: TEXT_REVEAL_Y }
                        }
                        animate={{ opacity: 1, y: 0 }}
                        exit={offsets ? { opacity: 0, y: offsets.exitY } : undefined}
                        transition={revealTransition(delayS + lineIdx * LINE_STAGGER_S)}
                        style={{ display: "block" }}
                    >
                        {line.map((fragment) => (
                            <StrongHTMLLineFragmentView
                                key={fragment.key}
                                fragment={fragment}
                            />
                        ))}
                    </motion.span>
                </span>
            ))}
        </span>
    );
}

function StrongHTMLLineFragmentView({
    fragment,
}: {
    fragment: StrongHTMLLineFragment;
}) {
    return (
        <span>
            {fragment.gapBefore > 0 ? " " : null}
            {fragment.strong ? (
                <strong className="font-semibold">{fragment.text}</strong>
            ) : (
                fragment.text
            )}
        </span>
    );
}

function parseStrongHtmlWordItems(
    html: string,
    measure: StrongHTMLLineMeasure,
): { items: RichInlineItem[]; strongByItemIndex: boolean[] } {
    const items: RichInlineItem[] = [];
    const strongByItemIndex: boolean[] = [];
    const parts = html.split(/(<strong>.*?<\/strong>)/);

    for (const part of parts) {
        if (!part) {
            continue;
        }

        const isStrong = part.startsWith("<strong>");
        const rawText = isStrong
            ? part.slice("<strong>".length, -"</strong>".length)
            : part;
        const font = isStrong ? measure.strongFont : measure.regularFont;
        const leadingSpace = rawText.match(STRONG_HTML_LEADING_SPACE_RE)?.[0] ?? "";
        const trailingSpace = rawText.match(STRONG_HTML_TRAILING_SPACE_RE)?.[0] ?? "";
        const coreText = rawText.trim();

        if (coreText.length === 0) {
            if (leadingSpace || trailingSpace) {
                strongByItemIndex.push(isStrong);
                items.push({ text: `${leadingSpace}${trailingSpace}`, font });
            }
            continue;
        }

        const words = splitPretextWords(coreText);
        for (let wordIndex = 0; wordIndex < words.length; wordIndex++) {
            const word = words[wordIndex];
            let text = word.text;

            if (wordIndex === 0) {
                text = `${leadingSpace}${text}`;
            }
            if (wordIndex < words.length - 1) {
                text = `${text} `;
            } else if (trailingSpace) {
                text = `${text}${trailingSpace}`;
            }

            strongByItemIndex.push(isStrong);
            items.push({ text, font });
        }
    }

    return { items, strongByItemIndex };
}

export function layoutStrongHTMLLines(
    html: string,
    measure: StrongHTMLLineMeasure,
): StrongHTMLLineFragment[][] {
    if (!html || measure.maxWidth <= 0) {
        return [];
    }

    const { items, strongByItemIndex } = parseStrongHtmlWordItems(html, measure);
    const prepared = prepareRichInline(items);
    const lines: StrongHTMLLineFragment[][] = [];

    walkRichInlineLineRanges(prepared, measure.maxWidth, (range) => {
        const materialized = materializeRichInlineLineRange(prepared, range);
        lines.push(
            materialized.fragments.map((fragment, fragmentIndex) => ({
                key: `${lines.length}:${fragmentIndex}`,
                text: fragment.text,
                strong: strongByItemIndex[fragment.itemIndex] ?? false,
                gapBefore: fragment.gapBefore,
            })),
        );
    });

    return lines;
}

function useStrongHTMLLines(
    html: string,
    measure: StrongHTMLLineMeasure,
): StrongHTMLLineFragment[][] {
    const { maxWidth, regularFont, strongFont } = measure;
    return layoutStrongHTMLLines(html, { maxWidth, regularFont, strongFont });
}

type AnimatedHTMLProps = {
    html: string;
    delayS?: number;
    className?: string;
};

export function AnimatedHTML({ html, delayS = 0, className }: AnimatedHTMLProps) {
    const tokens = useHTMLTokens(html);

    return (
        <span
            className={className}
            style={{ display: "flex", flexWrap: "wrap", gap: "0 4.5px" }}
        >
            {tokens.map((token, i) => (
                <span
                    key={token.id}
                    style={{
                        display: "inline-block",
                        verticalAlign: "bottom",
                    }}
                >
                    {token.isHTML ? (
                        <motion.span
                            initial={{ opacity: 0, y: TEXT_REVEAL_Y }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={revealTransition(delayS + i * WORD_STAGGER_S)}
                            style={{ display: "inline-block" }}
                            // biome-ignore lint/security/noDangerouslySetInnerHtml: trusted i18n HTML
                            dangerouslySetInnerHTML={{ __html: token.content }}
                        />
                    ) : (
                        <motion.span
                            initial={{ opacity: 0, y: TEXT_REVEAL_Y }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={revealTransition(delayS + i * WORD_STAGGER_S)}
                            style={{ display: "inline-block" }}
                        >
                            {token.content}
                        </motion.span>
                    )}
                </span>
            ))}
        </span>
    );
}

type AnimatedBlockProps = {
    delayS?: number;
    className?: string;
    direction?: AnimatedWordsDirection;
    children: ReactNode;
};

export function AnimatedBlock({
    delayS = 0,
    className,
    direction = "forward",
    children,
}: AnimatedBlockProps) {
    const enterY = direction === "forward" ? 18 : -18;

    return (
        <motion.div
            initial={{ opacity: 0, y: enterY }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
                opacity: { duration: 0.22, ease: "easeOut", delay: delayS },
                y: { duration: 0.32, ease: WORD_EASING, delay: delayS },
            }}
            className={className}
        >
            {children}
        </motion.div>
    );
}

export function nextDelay(text: string, baseDelayS: number): number {
    const wordCount = text.split(/\s+/).filter(Boolean).length;
    return baseDelayS + wordCount * WORD_STAGGER_S + BLOCK_GAP_S;
}

export function contentWidth(): number {
    return Math.min(520, window.innerWidth) - 48;
}

export type WordEntry = { id: string; text: string };

/** Groups consecutive non-whitespace Pretext segments into words. */
export function splitPretextWords(text: string): WordEntry[] {
    if (!text) return [];
    const prepared = prepareWithSegments(text, "16px sans-serif");
    const words: WordEntry[] = [];
    let current = "";
    let startPos = 0;
    let pos = 0;

    for (const seg of prepared.segments) {
        if (seg.trim().length === 0) {
            if (current) {
                words.push({ id: `${startPos}:${current}`, text: current });
                current = "";
            }
            pos += seg.length;
            startPos = pos;
        } else {
            if (!current) startPos = pos;
            current += seg;
            pos += seg.length;
        }
    }
    if (current) {
        words.push({ id: `${startPos}:${current}`, text: current });
    }

    return words;
}

function usePretextWords(text: string): WordEntry[] {
    return useMemo(() => splitPretextWords(text), [text]);
}

type LineEntry = { id: string; text: string; index: number };

function usePretextLines(
    text: string,
    font: string,
    maxWidth: number,
    lineHeight: number,
): LineEntry[] {
    return useMemo(() => {
        if (!text) return [];
        const prepared = prepareWithSegments(text, font);
        const result = layoutWithLines(prepared, maxWidth, lineHeight);
        return result.lines.map((l, i) => ({
            id: `${i}:${l.text}`,
            text: l.text,
            index: i,
        }));
    }, [text, font, maxWidth, lineHeight]);
}

type FragmentEntry = { key: string; itemIndex: number; text: string };

function useRichHTMLLines(
    html: string,
    font: string,
    maxWidth: number,
): { lines: FragmentEntry[][]; links: Map<number, LinkMeta> } {
    return useMemo(() => {
        if (!html) return { lines: [], links: new Map() };

        const items: RichInlineItem[] = [];
        const links = new Map<number, LinkMeta>();

        const parts = html.split(/(<a\s[^>]*>[^<]*<\/a>)/);
        for (const part of parts) {
            if (part.startsWith("<a ")) {
                const href = part.match(/href="([^"]*)"/)?.[1] ?? "";
                const cls = part.match(/class="([^"]*)"/)?.[1];
                const text = part.match(/>([^<]*)</)?.[1] ?? "";
                links.set(items.length, { href, className: cls });
                items.push({ text, font });
            } else if (part) {
                items.push({ text: part, font });
            }
        }

        const prepared = prepareRichInline(items);
        const lines: FragmentEntry[][] = [];
        walkRichInlineLineRanges(prepared, maxWidth, (range) => {
            const materialized = materializeRichInlineLineRange(prepared, range);
            lines.push(
                materialized.fragments.map((f, j) => ({
                    key: `${lines.length}:${j}`,
                    itemIndex: f.itemIndex,
                    text: f.text,
                })),
            );
        });

        return { lines, links };
    }, [html, font, maxWidth]);
}

type HTMLToken = { id: string; content: string; isHTML: boolean };

function useHTMLTokens(html: string): HTMLToken[] {
    return useMemo(() => {
        if (!html) return [];

        const tokens: HTMLToken[] = [];
        const parts = html.split(/(<[^>]+>[^<]*<\/[^>]+>)/);
        let globalPos = 0;

        for (const part of parts) {
            if (part.startsWith("<")) {
                tokens.push({ id: `el:${globalPos}`, content: part, isHTML: true });
                globalPos += part.length;
            } else {
                const prepared = prepareWithSegments(part, "16px sans-serif");
                let pos = globalPos;
                for (const seg of prepared.segments) {
                    if (seg.trim().length > 0) {
                        tokens.push({
                            id: `w:${pos}:${seg}`,
                            content: seg,
                            isHTML: false,
                        });
                    }
                    pos += seg.length;
                }
                globalPos = pos;
            }
        }

        return tokens;
    }, [html]);
}
